import React, { useState } from 'react';
import {
  Trophy, Award, Star, Medal, Crown, Zap, Target,
  Calendar, Users, Download, Share2, ExternalLink,
  ChevronRight, Filter, Search, Grid3x3, List,
  CheckCircle, Clock, Flame, Brain, BookOpen,
  TrendingUp, Shield, Heart, Coffee, Sparkles,
  Copy, Linkedin, Twitter, Facebook, Mail, Link2
} from 'lucide-react';

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  type: 'certificate' | 'badge' | 'milestone' | 'streak' | 'special';
  category: string;
  difficulty: 'bronze' | 'silver' | 'gold' | 'platinum';
  earnedDate: string;
  courseName?: string;
  points: number;
  rarity: number; // percentage of users who have this achievement
  sharable: boolean;
  verified: boolean;
}

interface Certificate {
  id: string;
  title: string;
  issuer: string;
  issueDate: string;
  expiryDate?: string;
  credentialId: string;
  verificationUrl: string;
  courseName: string;
  grade: string;
  hours: number;
  skills: string[];
  badgeUrl?: string;
}

interface Badge {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  earnedDate: string;
  requirement: string;
  progress?: number;
  maxProgress?: number;
}

export default function LearnerAchievements() {
  const [activeTab, setActiveTab] = useState<'overview' | 'certificates' | 'badges' | 'milestones'>('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);

  const overallStats = {
    totalAchievements: 47,
    certificates: 8,
    badges: 25,
    milestones: 14,
    totalPoints: 2450,
    currentLevel: 12,
    nextLevelPoints: 2800,
    completionRate: 68,
    rank: 145,
    totalUsers: 50000
  };

  const recentAchievements: Achievement[] = [
    {
      id: '1',
      title: 'React Mastery Certificate',
      description: 'Completed Advanced React Development course with distinction',
      icon: <Award className="w-6 h-6" />,
      type: 'certificate',
      category: 'Development',
      difficulty: 'gold',
      earnedDate: '2024-01-28',
      courseName: 'Advanced React Development',
      points: 500,
      rarity: 15,
      sharable: true,
      verified: true
    },
    {
      id: '2',
      title: 'Learning Streak Champion',
      description: '30-day consecutive learning streak achieved',
      icon: <Flame className="w-6 h-6" />,
      type: 'streak',
      category: 'Engagement',
      difficulty: 'silver',
      earnedDate: '2024-01-25',
      points: 200,
      rarity: 25,
      sharable: true,
      verified: true
    },
    {
      id: '3',
      title: 'Knowledge Explorer',
      description: 'Completed courses in 5 different categories',
      icon: <Brain className="w-6 h-6" />,
      type: 'milestone',
      category: 'Learning',
      difficulty: 'gold',
      earnedDate: '2024-01-20',
      points: 300,
      rarity: 35,
      sharable: true,
      verified: true
    },
    {
      id: '4',
      title: 'Perfect Score',
      description: 'Achieved 100% score in 3 consecutive assessments',
      icon: <Target className="w-6 h-6" />,
      type: 'badge',
      category: 'Performance',
      difficulty: 'platinum',
      earnedDate: '2024-01-15',
      points: 750,
      rarity: 8,
      sharable: true,
      verified: true
    }
  ];

  const certificates: Certificate[] = [
    {
      id: '1',
      title: 'Advanced React Development',
      issuer: 'Academy',
      issueDate: '2024-01-28',
      credentialId: 'CERT-REACT-2024-001',
      verificationUrl: 'https://verify.example.com/cert/CERT-REACT-2024-001',
      courseName: 'Advanced React Development',
      grade: 'A+ (95%)',
      hours: 24,
      skills: ['React Hooks', 'Performance Optimization', 'Testing', 'State Management']
    },
    {
      id: '2',
      title: 'Data Science Fundamentals',
      issuer: 'Academy',
      issueDate: '2024-01-15',
      credentialId: 'CERT-DS-2024-007',
      verificationUrl: 'https://verify.example.com/cert/CERT-DS-2024-007',
      courseName: 'Data Science Fundamentals',
      grade: 'A (88%)',
      hours: 32,
      skills: ['Python', 'Pandas', 'Data Visualization', 'Statistics']
    },
    {
      id: '3',
      title: 'Leadership Excellence',
      issuer: 'Academy',
      issueDate: '2024-01-10',
      expiryDate: '2027-01-10',
      credentialId: 'CERT-LEAD-2024-023',
      verificationUrl: 'https://verify.example.com/cert/CERT-LEAD-2024-023',
      courseName: 'Leadership Excellence Program',
      grade: 'A (92%)',
      hours: 16,
      skills: ['Team Management', 'Communication', 'Strategic Thinking', 'Decision Making']
    }
  ];

  const badges: Badge[] = [
    {
      id: '1',
      name: 'Early Bird',
      description: 'Complete 10 lessons before 9 AM',
      icon: <Coffee className="w-5 h-5" />,
      color: 'from-orange-400 to-yellow-500',
      earnedDate: '2024-01-26',
      requirement: 'Complete 10 lessons before 9 AM',
      progress: 10,
      maxProgress: 10
    },
    {
      id: '2',
      name: 'Speed Learner',
      description: 'Complete a course in under 2 days',
      icon: <Zap className="w-5 h-5" />,
      color: 'from-blue-400 to-purple-500',
      earnedDate: '2024-01-22',
      requirement: 'Complete a full course in under 48 hours'
    },
    {
      id: '3',
      name: 'Community Helper',
      description: 'Help 5 fellow learners in discussions',
      icon: <Heart className="w-5 h-5" />,
      color: 'from-pink-400 to-red-500',
      earnedDate: '2024-01-18',
      requirement: 'Provide helpful answers in 5 course discussions'
    },
    {
      id: '4',
      name: 'Knowledge Seeker',
      description: 'Complete 100 lessons across all courses',
      icon: <BookOpen className="w-5 h-5" />,
      color: 'from-green-400 to-emerald-500',
      earnedDate: '2024-01-14',
      requirement: 'Complete 100+ lessons',
      progress: 100,
      maxProgress: 100
    }
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'bronze': return 'from-amber-600 to-yellow-600';
      case 'silver': return 'from-gray-400 to-gray-600';
      case 'gold': return 'from-yellow-400 to-orange-500';
      case 'platinum': return 'from-purple-400 to-indigo-500';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const handleShare = (achievement: Achievement) => {
    setSelectedAchievement(achievement);
    setShowShareModal(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // In a real app, you'd show a toast notification here
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-yellow-500 to-brand-orange-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <Trophy className="w-7 h-7" />
              Achievements & Certificates
            </h1>
            <p className="text-brand-yellow-100">
              Showcase your learning accomplishments and professional certifications
            </p>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors">
              <Download className="w-5 h-5" />
              <span className="hidden sm:inline">Export Portfolio</span>
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-white text-brand-orange-600 rounded-lg hover:bg-brand-orange-50 transition-colors font-medium">
              <ExternalLink className="w-5 h-5" />
              <span className="hidden sm:inline">Public Profile</span>
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Trophy className="w-6 h-6 mx-auto mb-2 text-brand-yellow-200" />
            <p className="text-2xl font-bold">{overallStats.totalAchievements}</p>
            <p className="text-xs text-brand-yellow-100">Total Achievements</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Award className="w-6 h-6 mx-auto mb-2 text-brand-yellow-200" />
            <p className="text-2xl font-bold">{overallStats.certificates}</p>
            <p className="text-xs text-brand-yellow-100">Certificates</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Medal className="w-6 h-6 mx-auto mb-2 text-brand-yellow-200" />
            <p className="text-2xl font-bold">{overallStats.badges}</p>
            <p className="text-xs text-brand-yellow-100">Badges</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Star className="w-6 h-6 mx-auto mb-2 text-brand-yellow-200" />
            <p className="text-2xl font-bold">{overallStats.totalPoints.toLocaleString()}</p>
            <p className="text-xs text-brand-yellow-100">Total Points</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Crown className="w-6 h-6 mx-auto mb-2 text-brand-yellow-200" />
            <p className="text-2xl font-bold">#{overallStats.rank}</p>
            <p className="text-xs text-brand-yellow-100">Global Rank</p>
          </div>
        </div>

        {/* Level Progress */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm">Level {overallStats.currentLevel}</span>
            <span className="text-sm">Level {overallStats.currentLevel + 1}</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-3">
            <div 
              className="bg-white rounded-full h-3 transition-all"
              style={{ 
                width: `${(overallStats.totalPoints / overallStats.nextLevelPoints) * 100}%` 
              }}
            />
          </div>
          <p className="text-xs text-brand-yellow-100 mt-1">
            {overallStats.nextLevelPoints - overallStats.totalPoints} points to next level
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex">
            {[
              { key: 'overview', label: 'Overview', icon: Trophy, count: null },
              { key: 'certificates', label: 'Certificates', icon: Award, count: certificates.length },
              { key: 'badges', label: 'Badges', icon: Medal, count: badges.length },
              { key: 'milestones', label: 'Milestones', icon: Target, count: 14 }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex items-center gap-2 px-6 py-3 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.key
                    ? 'border-brand-orange-500 text-brand-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== null && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Recent Achievements */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Recent Achievements</h2>
                  <button className="text-sm text-brand-orange-600 hover:text-brand-orange-700 flex items-center gap-1">
                    View all <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {recentAchievements.map((achievement) => (
                    <div key={achievement.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all">
                      <div className="flex items-start gap-3">
                        <div className={`p-3 bg-gradient-to-r ${getDifficultyColor(achievement.difficulty)} rounded-lg text-white flex-shrink-0`}>
                          {achievement.icon}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-medium text-gray-900">{achievement.title}</h3>
                              <p className="text-sm text-gray-600">{achievement.description}</p>
                            </div>
                            {achievement.verified && (
                              <Shield className="w-4 h-4 text-blue-500" title="Verified Achievement" />
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span>{new Date(achievement.earnedDate).toLocaleDateString()}</span>
                              <span>{achievement.points} pts</span>
                              <span>{achievement.rarity}% have this</span>
                            </div>
                            {achievement.sharable && (
                              <button
                                onClick={() => handleShare(achievement)}
                                className="p-1.5 text-gray-400 hover:text-brand-orange-600 rounded-lg hover:bg-brand-orange-50"
                              >
                                <Share2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Achievement Categories */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Achievement Categories</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6">
                    <BookOpen className="w-8 h-8 text-blue-600 mb-3" />
                    <h3 className="font-semibold text-gray-900 mb-1">Learning</h3>
                    <p className="text-sm text-gray-600 mb-3">Course completion and knowledge milestones</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-gray-900">18</span>
                      <span className="text-xs text-gray-500">achievements</span>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6">
                    <Target className="w-8 h-8 text-green-600 mb-3" />
                    <h3 className="font-semibold text-gray-900 mb-1">Performance</h3>
                    <p className="text-sm text-gray-600 mb-3">High scores and perfect completions</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-gray-900">12</span>
                      <span className="text-xs text-gray-500">achievements</span>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6">
                    <Flame className="w-8 h-8 text-purple-600 mb-3" />
                    <h3 className="font-semibold text-gray-900 mb-1">Engagement</h3>
                    <p className="text-sm text-gray-600 mb-3">Streaks and consistent learning</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-gray-900">17</span>
                      <span className="text-xs text-gray-500">achievements</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'certificates' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Professional Certificates</h2>
                <button className="px-4 py-2 bg-brand-orange-600 text-white rounded-lg hover:bg-brand-orange-700 font-medium">
                  Add to LinkedIn
                </button>
              </div>
              
              <div className="space-y-4">
                {certificates.map((cert) => (
                  <div key={cert.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-gradient-to-r from-brand-blue-500 to-brand-indigo-600 rounded-lg text-white">
                          <Award className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{cert.title}</h3>
                          <p className="text-sm text-gray-600">{cert.issuer}</p>
                          <p className="text-xs text-gray-500">
                            Issued {new Date(cert.issueDate).toLocaleDateString()}
                            {cert.expiryDate && ` • Expires ${new Date(cert.expiryDate).toLocaleDateString()}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copyToClipboard(cert.verificationUrl)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                          title="Copy verification link"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                          <Download className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                          <Share2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Grade Achieved</p>
                        <p className="font-medium text-gray-900">{cert.grade}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Duration</p>
                        <p className="font-medium text-gray-900">{cert.hours} hours</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Credential ID</p>
                        <p className="font-medium text-gray-900 font-mono text-sm">{cert.credentialId}</p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <p className="text-xs text-gray-500 mb-2">Skills Validated</p>
                      <div className="flex flex-wrap gap-2">
                        {cert.skills.map((skill, index) => (
                          <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <a
                        href={cert.verificationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-brand-blue-600 hover:text-brand-blue-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Verify Certificate
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'badges' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Achievement Badges</h2>
                <div className="flex items-center gap-2">
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="all">All Categories</option>
                    <option value="learning">Learning</option>
                    <option value="engagement">Engagement</option>
                    <option value="performance">Performance</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {badges.map((badge) => (
                  <div key={badge.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                    <div className="text-center">
                      <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r ${badge.color} flex items-center justify-center text-white`}>
                        {badge.icon}
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1">{badge.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{badge.description}</p>
                      
                      {badge.progress && badge.maxProgress && (
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-500">Progress</span>
                            <span className="text-xs font-medium">{badge.progress}/{badge.maxProgress}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full bg-gradient-to-r ${badge.color}`}
                              style={{ width: `${(badge.progress / badge.maxProgress) * 100}%` }}
                            />
                          </div>
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-500 mb-2">
                        Earned {new Date(badge.earnedDate).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-gray-400">{badge.requirement}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'milestones' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Learning Milestones</h2>
              </div>

              <div className="relative">
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                <div className="space-y-6">
                  {[
                    { date: '2024-01-28', title: 'React Expert Level Reached', description: 'Completed all advanced React courses with distinction', icon: <Crown className="w-5 h-5" />, color: 'bg-yellow-500' },
                    { date: '2024-01-25', title: '30-Day Learning Streak', description: 'Maintained consistent daily learning for a full month', icon: <Flame className="w-5 h-5" />, color: 'bg-orange-500' },
                    { date: '2024-01-20', title: 'Multi-Disciplinary Learner', description: 'Completed courses in 5 different subject areas', icon: <Brain className="w-5 h-5" />, color: 'bg-purple-500' },
                    { date: '2024-01-15', title: '100th Lesson Completed', description: 'Reached the century mark in total lessons completed', icon: <BookOpen className="w-5 h-5" />, color: 'bg-blue-500' },
                    { date: '2024-01-10', title: 'First Perfect Score', description: 'Achieved 100% score on a challenging assessment', icon: <Target className="w-5 h-5" />, color: 'bg-green-500' },
                    { date: '2024-01-05', title: 'Community Contributor', description: 'Helped fellow learners by answering questions', icon: <Heart className="w-5 h-5" />, color: 'bg-pink-500' }
                  ].map((milestone, index) => (
                    <div key={index} className="relative flex items-start gap-6">
                      <div className={`relative z-10 ${milestone.color} rounded-full p-3 text-white shadow-lg`}>
                        {milestone.icon}
                      </div>
                      <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium text-gray-900">{milestone.title}</h3>
                            <p className="text-sm text-gray-600 mt-1">{milestone.description}</p>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(milestone.date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && selectedAchievement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 common-modal-overlay">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Share Achievement</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="text-center mb-6">
              <div className={`w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-r ${getDifficultyColor(selectedAchievement.difficulty)} flex items-center justify-center text-white`}>
                {selectedAchievement.icon}
              </div>
              <h4 className="font-medium text-gray-900">{selectedAchievement.title}</h4>
              <p className="text-sm text-gray-600 mt-1">{selectedAchievement.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button className="flex items-center justify-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <Linkedin className="w-5 h-5 text-blue-600" />
                LinkedIn
              </button>
              <button className="flex items-center justify-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <Twitter className="w-5 h-5 text-blue-400" />
                Twitter
              </button>
              <button className="flex items-center justify-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <Facebook className="w-5 h-5 text-blue-800" />
                Facebook
              </button>
              <button className="flex items-center justify-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <Mail className="w-5 h-5 text-gray-600" />
                Email
              </button>
            </div>

            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={`Check out my latest achievement: ${selectedAchievement.title}!`}
                  readOnly
                  className="flex-1 bg-transparent text-sm text-gray-600"
                />
                <button
                  onClick={() => copyToClipboard(`Check out my latest achievement: ${selectedAchievement.title}!`)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}